import $ from 'jquery';
import _ from 'underscore';

// @ts-expect-error
import {PrimerPlugin, primerPlugin, replacePrimerRegion} from 'assembly/_primer-plugin';
// @ts-expect-error
import {addPrimerNameSuffixes, getFlankingPrimers, validSpacerOrder} from 'assembly/_shared';
import {Assembly, AssemblySlotType} from 'assembly/AssemblyModels';
// @ts-expect-error
import GoldenGateTabState from 'assembly/tabs/GoldenGateTabState';
import {assert} from 'Asserts';
import * as biojs from 'BioJS';
import {NucleotideType, DNA_COMPLEMENT_TABLE} from 'biojs/BioConstants';
import * as util from 'biojs/Util';
import {Selection} from 'components/Sequence';
import {ASSEMBLY_TYPE} from 'constants/AssemblyConstants';
import * as colors from 'lib/Colors';
import Enzymes, {Enzyme} from 'molbio/enzymes/backbone/Enzymes';
import * as sequtil from 'molbio/SeqUtil';
import {BufferState} from 'Navigation';
import * as logging from 'util/Logging';

const COMMON_ENZYMES = Enzymes.getCommonEnzymes();

const MIN_HAMMING_DISTANCE = 2;

// Keep in sync with values in benchling/molbio/bulk_workflows/assembly/primer_design/golden_gate
const WARNING_VALIDATION_SCORE = 1;
const ERROR_VALIDATION_SCORE = 1000;

const MAX_DEV = 15;
const MAX_ITERATION_NUMBER = 1000;

class AssemblyError extends Error {}

function throwAssemblyError(message: string): void {
  throw new AssemblyError(message);
}

// Returns [digestedFragments, hasStickyEnds]
function slotsToFragments(slots: Array<AssemblySlotType>): [Array<biojs.Sequence | null>, Array<boolean>] {
  // _.zip allows us to transform
  //   [[fragA, boolA], [fragB, boolB], [fragC, boolC]]
  //   ->[[fragA, fragB, fragC], [boolA, boolB, boolC]]
  const result = _.zip(
    ...slots.map((slot) => {
      let seqFragment;
      assert(!slot.isSpacer(), 'Expected a fragment slot');
      if (slot.isEmpty()) {
        return [null, false];
      }
      const fragment = slot.singleFragment();
      const sequence = fragment.getSequence();
      // Hasn't loaded yet.
      if (sequence == null) {
        return [null, false];
      }
      const usesEnzymes = fragment.enzymeIDs() != null;
      if (usesEnzymes) {
        seqFragment = fragment.getSelectedFragmentFromSequence(sequence)!;
        // We don't use offsets for GG assembly.
        seqFragment.set({offset5: 0, offset3: 0});
      } else {
        const start = fragment.start()!;
        const end = fragment.end()!;
        const createOptions = {annotations: true, notcontained: true};
        seqFragment = sequence.get('_sequence').createFromIndices(start, end, createOptions);
      }
      if (fragment.reverse()) {
        seqFragment = seqFragment.cloneReverseComplement();
      }
      seqFragment.set({name: fragment.getSequence()!.name()});
      return [seqFragment, usesEnzymes];
    })
  );
  // Handle empty array.
  if (result.length === 0) {
    return [[], []];
  }
  return result as [Array<biojs.Sequence | null>, Array<boolean>];
}

// Checks whether s1 and s2 share a contiguous 3 bp substring
function _stickyEndsContiguousOverlap(s1: string, s2: string): boolean {
  return s2.indexOf(s1.slice(1)) !== -1 || s2.indexOf(s1.slice(0, -1)) !== -1;
}

export interface FinalizedStickyEndValidationResult {
  validationScore: number;
}

interface DryRunStickyEndValidationResult {
  errors: Array<string>;
  warnings: Array<string>;
}

type StickyEndValidationResult = FinalizedStickyEndValidationResult | DryRunStickyEndValidationResult;

function validateStickyEnds(
  junctionStickyEnds: Array<StickyEnd>,
  internalStickyEnds: Array<StickyEnd>,
  dryRun?: boolean | null
): StickyEndValidationResult {
  // Don't count the assembly error if the invalid sticky ends are pre-existing or internal, since
  // we show the error to the users in the assembly view and Assembly tab, but proceed with
  // designing Golden Gate sticky ends anyway.
  let msg;
  if (dryRun == null) {
    dryRun = false;
  }
  const errors: Array<string> = [];
  const warnings = [];
  let validationScore = 0;
  for (const stickyEnd of junctionStickyEnds) {
    const gc = util.gc(stickyEnd.bases, util.NucleotideType.DNA);
    if (gc === 0 || gc === 1) {
      msg = `Sticky end ${stickyEnd.friendly()} has GC content of ${gc === 0 ? 0 : 100}%.`;
      if (dryRun) {
        warnings.push(msg);
      } else if (!stickyEnd.preExisting) {
        validationScore += WARNING_VALIDATION_SCORE;
      }
    }
  }

  // Note the fact that stickyEnds is [junctionStickyEnds...internalStickyEnds] is important for
  // isInternalStickyEnd.
  const stickyEnds = junctionStickyEnds.concat(internalStickyEnds);
  const isInternalStickyEnd = (i: number): boolean => i >= junctionStickyEnds.length;
  for (let i1 = 0; i1 < stickyEnds.length; i1++) {
    const stickyEnd1 = stickyEnds[i1];
    for (let i2 = 0; i2 < stickyEnds.length; i2++) {
      // Don't want to duplicate errors / warnings.
      const rawStickyEnd2 = stickyEnds[i2];
      if (i2 < i1) {
        continue;
      }
      // Check stickyEnd1 against rawStickyEnd2 (except when i1 = i2) and its reverse complement.
      const stickyEnds2 = (i1 === i2 ? [] : [rawStickyEnd2]).concat([rawStickyEnd2.revcomp()]);
      for (const stickyEnd2 of stickyEnds2) {
        const isTooSimilar = _stickyEndsContiguousOverlap(stickyEnd1.bases, stickyEnd2.bases);
        const hammingDistance = util.hammingDistance(stickyEnd1.bases, stickyEnd2.bases);
        if (isTooSimilar || hammingDistance < MIN_HAMMING_DISTANCE) {
          const isIdentical = hammingDistance === 0;
          const isInternal1 = isInternalStickyEnd(i1);
          const isInternal2 = isInternalStickyEnd(i2);
          const problem = isIdentical ? 'identical' : isTooSimilar ? 'too similar' : 'similar';

          if (isInternal1 && isInternal2) {
            msg = `Internal sticky ends ${stickyEnd1.friendly()} and ${stickyEnd2.friendly()} are \
${problem}.`;
          } else if (isInternal1) {
            msg = `Sticky end ${stickyEnd2.friendly()} is ${problem} to internal sticky end \
${stickyEnd1.friendly()}.`;
          } else if (isInternal2) {
            msg = `Sticky end ${stickyEnd1.friendly()} is ${problem} to internal sticky end \
${stickyEnd2.friendly()}.`;
          } else {
            msg = `Sticky ends ${stickyEnd1.friendly()} and ${stickyEnd2.friendly()} are \
${problem}.`;
          }

          if (dryRun) {
            (isTooSimilar ? errors : warnings).push(msg);
          } else if (!(isInternal1 && isInternal2) && !(stickyEnd1.preExisting && stickyEnd2.preExisting)) {
            validationScore += isTooSimilar ? ERROR_VALIDATION_SCORE : WARNING_VALIDATION_SCORE;
          }
        }
      }
    }
  }
  if (dryRun) {
    return {errors, warnings};
  }
  return {validationScore};
}

function tm(bases: string): number {
  return util.meltingTemperature(bases, NucleotideType.DNA);
}

interface PrimerSpecification {
  bases: string;
  bindingLength: number;
}

function _getPrimerPair(
  fragmentBases: string,
  enzyme: Enzyme,
  options: FilledGoldenGateOptions & {
    postRecognitionSiteBases: string;
    forwardPreBindingRegionBases: string;
    reversePreBindingRegionBases: string;
  }
): {forward: PrimerSpecification; reverse: PrimerSpecification} {
  let forwardBindingRegion = '';
  let forwardBindingRegionLength = 0;
  const getForwardBindingRegion = (regionLength: number): string => fragmentBases.slice(0, regionLength);
  let reverseBindingRegion = '';
  let reverseBindingRegionLength = 0;
  const getReverseBindingRegion = (regionLength: number): string =>
    fragmentBases.slice(fragmentBases.length - regionLength);

  if (options.bindingRegionMinLength > fragmentBases.length) {
    throwAssemblyError(
      `Fragment with is too short for minimum binding region length of ${options.bindingRegionMinLength}.`
    );
  }
  // Determine the binding region of each primer from min length and Tm options.
  for (
    forwardBindingRegionLength = options.bindingRegionMinLength;
    forwardBindingRegionLength < fragmentBases.length;
    forwardBindingRegionLength++
  ) {
    forwardBindingRegion = getForwardBindingRegion(forwardBindingRegionLength);
    if (tm(forwardBindingRegion) >= options.bindingRegionMinTM) {
      break;
    }
  }
  for (
    reverseBindingRegionLength = options.bindingRegionMinLength;
    reverseBindingRegionLength < fragmentBases.length;
    reverseBindingRegionLength++
  ) {
    reverseBindingRegion = getReverseBindingRegion(reverseBindingRegionLength);
    if (tm(reverseBindingRegion) >= options.bindingRegionMinTM) {
      break;
    }
  }
  if (Math.min(tm(reverseBindingRegion), tm(forwardBindingRegion)) < options.bindingRegionMinTM) {
    throwAssemblyError(`Unable to design primers with binding region melting temperatures over \
${options.bindingRegionMinTM} C.`);
  }

  const getForwardPrimer = (): string =>
    options.preRecognitionSiteBases +
    enzyme.get('seq') +
    options.postRecognitionSiteBases +
    options.forwardPreBindingRegionBases +
    forwardBindingRegion;
  const getReversePrimer = (): string =>
    options.preRecognitionSiteBases +
    enzyme.get('seq') +
    options.postRecognitionSiteBases +
    util.revcomp(options.reversePreBindingRegionBases, DNA_COMPLEMENT_TABLE) +
    util.revcomp(reverseBindingRegion, DNA_COMPLEMENT_TABLE);

  // Now, determine the binding region of each primer from max Tm diff option.
  let forwardPrimer = getForwardPrimer();
  let reversePrimer = getReversePrimer();
  let forwardBindingTM = tm(forwardBindingRegion);
  let reverseBindingTM = tm(reverseBindingRegion);
  const primerPairTMDiff = (): number => Math.abs(forwardBindingTM - reverseBindingTM);
  while (primerPairTMDiff() > options.primerPairMaxTMDiff) {
    if (forwardBindingTM < reverseBindingTM) {
      if (forwardBindingRegionLength === fragmentBases.length) {
        break;
      }
      forwardBindingRegionLength++;
      forwardBindingRegion = getForwardBindingRegion(forwardBindingRegionLength);
      forwardPrimer = getForwardPrimer();
      forwardBindingTM = tm(forwardBindingRegion);
    } else {
      if (reverseBindingRegionLength === fragmentBases.length) {
        break;
      }
      reverseBindingRegionLength++;
      reverseBindingRegion = getReverseBindingRegion(reverseBindingRegionLength);
      reversePrimer = getReversePrimer();
      reverseBindingTM = tm(reverseBindingRegion);
    }
  }
  if (primerPairTMDiff() > options.primerPairMaxTMDiff) {
    throwAssemblyError(`Unable to design primers with a melting temperature difference under \
${options.primerPairMaxTMDiff} C.`);
  }

  return {
    forward: {bases: forwardPrimer, bindingLength: forwardBindingRegionLength},
    reverse: {bases: reversePrimer, bindingLength: reverseBindingRegionLength},
  };
}

class StickyEnd {
  bases: string;
  fragmentName: string | null;
  preExisting: boolean;

  constructor(bases: string, fragmentName: string | null = null, preExisting: boolean | null = null) {
    this.fragmentName = fragmentName;
    if (preExisting == null) {
      preExisting = false;
    }
    this.preExisting = preExisting;
    this.bases = util.normalize(bases);
  }

  friendly(): string {
    if (this.fragmentName != null) {
      return `${this.bases} (${this.fragmentName})`;
    }
    return this.bases;
  }

  revcomp(): StickyEnd {
    return new StickyEnd(
      util.revcomp(this.bases, DNA_COMPLEMENT_TABLE),
      `${this.fragmentName}, rev comp`,
      this.preExisting
    );
  }
}

function _makePrevIndexFunc(
  array: Array<unknown> | Backbone.Collection<Backbone.Model>
): (index: number) => number {
  return (index: number) => (index === 0 ? array.length - 1 : index - 1);
}

function _makeNextIndexFunc(
  array: Array<unknown> | Backbone.Collection<Backbone.Model>
): (index: number) => number {
  return (index: number) => (index + 1) % array.length;
}

function _spacerBasesAt(spacers: Array<biojs.Sequence | null>, index: number): string {
  return (spacers[index] != null ? spacers[index]!.bases() : undefined) || '';
}

function _startStickyEnd(fragment: biojs.Sequence, enzyme: Enzyme, name: string | null = null): StickyEnd {
  const bases = fragment.bases().slice(0, enzyme.stickyEndLength());
  return new StickyEnd(bases, name || fragment.name(), true);
}

function _endStickyEnd(fragment: biojs.Sequence, enzyme: Enzyme, name: string | null = null): StickyEnd {
  const bases = fragment.bases().slice(fragment.length() - enzyme.stickyEndLength());
  return new StickyEnd(bases, name || fragment.name(), true);
}

function _getPreExistingJunctionStickyEnds(
  fragments: Array<biojs.Sequence | null>,
  hasStickyEnds: Array<boolean>,
  enzyme: Enzyme
): Array<StickyEnd | null> {
  const prev = _makePrevIndexFunc(fragments);
  const junctionStickyEnds = [];
  for (let i = 0; i < fragments.length; i++) {
    let name = null;
    if (fragments[prev(i)] != null && fragments[i] != null) {
      name = `${fragments[prev(i)]!.name()} / ${fragments[i]!.name()}`;
    }

    if (hasStickyEnds[i]) {
      junctionStickyEnds.push(_startStickyEnd(fragments[i]!, enzyme, name));
    } else if (hasStickyEnds[prev(i)]) {
      junctionStickyEnds.push(_endStickyEnd(fragments[prev(i)]!, enzyme, name));
    } else {
      junctionStickyEnds.push(null);
    }
  }
  return junctionStickyEnds;
}

interface StickyEndPart {
  prevFragmentStart: number;
  prevFragmentEnd: number;
  fragmentStart: number;
  fragmentEnd: number;
  prevSpacerStart: number;
  prevSpacerEnd: number;
  stickyEnd: StickyEnd;
}

function _getJunctionStickyEndParts(
  fragments: Array<biojs.Sequence | null>,
  hasStickyEnds: Array<boolean>,
  spacers: Array<biojs.Sequence | null>,
  enzyme: Enzyme,
  stickyEndCenterMap: (index: number) => number
): Array<StickyEndPart> | null {
  // Generate the junction sticky ends between fragments using stickyEndCenterMap to calculate
  // where to set the center of each sticky end, relative to the middle of the space between each
  // pair of fragments (at the boundary if there is no spacer, or in the middle of the spacer if
  // there is a spacer).
  //
  // When generating the i'th sticky end (between the (i - 1)'th and i'th fragments), track and
  // return the indices from each component used in the sticky end. Example diagram:
  //
  //   | Previous Fragment | Spacer | Fragment |
  //                    A  B     C
  //                    | Sticky |
  //                    | End    |
  // Here, A...B is from Previous Fragment, B...C is from Spacer, but nothing is from Fragment.
  const prev = _makePrevIndexFunc(fragments);

  const preExistingJunctionStickyEnds = _getPreExistingJunctionStickyEnds(fragments, hasStickyEnds, enzyme);

  const stickyEndParts = fragments.map((fragment, i) => {
    let stickyEnd;
    const prevFragment = fragments[prev(i)]!;
    const prevSpacerBases = _spacerBasesAt(spacers, prev(i));
    const preExistingStickyEnd = preExistingJunctionStickyEnds[i];

    if (hasStickyEnds[i]) {
      return {
        prevFragmentStart: prevFragment.length(),
        prevFragmentEnd: prevFragment.length(),
        fragmentStart: 0,
        fragmentEnd: enzyme.stickyEndLength(),
        prevSpacerStart: prevSpacerBases.length,
        prevSpacerEnd: prevSpacerBases.length,
        stickyEnd: preExistingStickyEnd,
      };
    }

    if (hasStickyEnds[prev(i)]) {
      return {
        prevFragmentStart: prevFragment.length() - enzyme.stickyEndLength(),
        prevFragmentEnd: prevFragment.length(),
        fragmentStart: 0,
        fragmentEnd: 0,
        prevSpacerStart: 0,
        prevSpacerEnd: 0,
        stickyEnd: preExistingStickyEnd,
      };
    }

    // Diagram:
    //   | Previous Fragment | Spacer | Fragment |
    //        A   B    C     a    b   c
    //        | Sticky |
    //        | End    |
    // Here, a = prevSpacerLeft, b = 0, c = prevSpacerRight, A = left, B = center, and C = right.
    const prevSpacerLeft = 0 - Math.ceil(prevSpacerBases.length / 2);
    const prevSpacerRight = 0 + Math.floor(prevSpacerBases.length / 2);
    assert(prevSpacerLeft <= prevSpacerRight, 'Expected prevSpacerLeft <= prevSpacerRight');

    const center = stickyEndCenterMap(i);
    const left = center - Math.floor(enzyme.stickyEndLength() / 2);
    const right = center + Math.ceil(enzyme.stickyEndLength() / 2);
    assert(enzyme.stickyEndLength() > 0, `stickyEndLength=${enzyme.stickyEndLength()} > 0 false.`);
    assert(left < right, `left=${left} < right=${right} false.`);
    const prevFragmentStart = prevFragment.length() + Math.min(0, left - prevSpacerLeft);
    const prevFragmentEnd = prevFragment.length() + Math.min(0, right - prevSpacerLeft);
    assert(prevFragmentStart <= prevFragmentEnd, 'Expected prevFragmentStart <= prevFragmentEnd');
    if (prevFragmentStart < 0) {
      return null;
    }
    const fragmentStart = 0 + Math.max(0, left - prevSpacerRight);
    const fragmentEnd = 0 + Math.max(0, right - prevSpacerRight);
    assert(fragmentStart <= fragmentEnd, 'Expected fragmentStart <= fragmentEnd');
    if (fragmentEnd > fragment!.length()) {
      return null;
    }
    // Clamp left and right to [prevSpacerLeft, prevSpacerRight] and map to [0, spacerLength]
    const prevSpacerStart = Math.min(prevSpacerRight, Math.max(prevSpacerLeft, left)) - prevSpacerLeft;
    const prevSpacerEnd = Math.min(prevSpacerRight, Math.max(prevSpacerLeft, right)) - prevSpacerLeft;
    assert(
      prevSpacerStart <= prevSpacerEnd,

      (stickyEnd =
        prevFragment.bases().slice(prevFragmentStart, prevFragmentEnd) +
        prevSpacerBases.slice(prevSpacerStart, prevSpacerEnd) +
        fragment!.bases().slice(fragmentStart, fragmentEnd))
    );
    return {
      prevFragmentStart,
      prevFragmentEnd,
      fragmentStart,
      fragmentEnd,
      prevSpacerStart,
      prevSpacerEnd,
      stickyEnd: new StickyEnd(stickyEnd),
    };
  });

  if (stickyEndParts.some((stickyEnd) => stickyEnd == null)) {
    return null;
  }
  return stickyEndParts as Array<StickyEndPart>;
}

function getDeviationsIndexMap(
  length: number,
  hasStickyEnds: Array<boolean>,
  prev: (i: number) => number
): {[idx: number]: number} {
  const devsIndexMap: {[idx: number]: number} = {};
  let devsIndex = 0;
  for (let i = 0; i < length; i++) {
    if (hasStickyEnds[i] || hasStickyEnds[prev(i)]) {
      continue;
    }
    devsIndexMap[i] = devsIndex;
    devsIndex++;
  }
  return devsIndexMap;
}

function _designStickyEnds(
  fragments: Array<biojs.Sequence | null>,
  hasStickyEnds: Array<boolean>,
  spacers: Array<biojs.Sequence | null>,
  enzyme: Enzyme,
  internalStickyEnds: Array<StickyEnd>
): JQueryPromise<Array<StickyEndPart> | null> | JQueryPromise<void> {
  // Generate the junction sticky ends using a trial-and-error method. The goal is to get the
  // sticky ends to come from the middle of the space between each pair of fragments, or as close
  // as possible. Therefore, for each pair, we try taking sticky ends from the middle, one to the
  // left, one to the right, two to the left, etc. This is represented by deviation dev = 0, -1,
  // +1, -2... and devNum = 0, 1, 2, 3... (i.e., dev mapped to a natural number). To systematically
  // iterate over all possibilities, we constrain the max deviation at first and then increment
  // it.
  let stickyEndParts;
  const prev = _makePrevIndexFunc(fragments);
  const deferred: JQueryDeferred<Array<StickyEndPart> | null> = $.Deferred();

  const devsIndexMap = getDeviationsIndexMap(fragments.length, hasStickyEnds, prev);

  // eslint-disable-next-line no-restricted-syntax
  const devsLength = _.size(devsIndexMap);
  if (devsLength === 0) {
    stickyEndParts = _getJunctionStickyEndParts(fragments, hasStickyEnds, spacers, enzyme, (i) => {
      throw new Error('Unexpected call to stickyEndCenterMap.');
    });
    return deferred.resolve(stickyEndParts).promise();
  }
  const devs: Array<number> = new Array(devsLength);
  let bestValidationScore = Infinity;
  let bestStickyEndParts: Array<StickyEndPart> | null = null;

  deferred.reject();
  let designPromise: JQueryPromise<Array<StickyEndPart> | null> = deferred;
  for (let maxDev = 0; maxDev < MAX_DEV; maxDev++) {
    designPromise = designPromise.then(
      // @ts-expect-error: types/jquery does not allow null
      null,
      // eslint-disable-next-line no-loop-func
      () => {
        const rawPercentComplete = (100.0 * maxDev) / MAX_DEV;
        // Scale raw % complete. Since the first few iterations are more likely to find sticky end
        // parts, the % complete increments more for them.
        const percentComplete = 100.0 * Math.pow(Math.sin(0.005 * Math.PI * rawPercentComplete), 0.333);
        const newDeferred = $.Deferred().notify(percentComplete);
        _.defer(() => {
          let devsIndex;
          const minDev = -maxDev;
          const maxDevNum = 2 * maxDev + 1;
          // Pre-compute powers.
          const pows = [];
          for (devsIndex = 0; devsIndex <= devsLength; devsIndex++) {
            pows.push(Math.pow(maxDevNum, devsIndex));
          }
          // Iterate over all maxDevNum ^ devsLength possibilities.
          for (
            let iterationNumber = 0;
            iterationNumber < Math.min(pows[devsLength], MAX_ITERATION_NUMBER);
            iterationNumber++
          ) {
            if (pows[devsLength] <= MAX_ITERATION_NUMBER) {
              // Map iterationNumber to array of fragment deviations.
              for (devsIndex = 0; devsIndex < devsLength; devsIndex++) {
                const devNum = Math.floor((iterationNumber % pows[devsIndex + 1]) / pows[devsIndex]);
                // [0, 1, 2, 3, 4...] -> [0, -1, 1, -2, 2...]
                const dev = devNum % 2 === 0 ? devNum / 2 : (devNum + 1) / -2;
                devs[devsIndex] = dev;
              }
              // If every deviation is less than maxDev, we've already tried it.
              if (devs.every((d) => minDev < d && d < maxDev)) {
                continue;
              }
            } else {
              // Fill array of random fragment deviations.
              for (devsIndex = 0; devsIndex < devsLength; devsIndex++) {
                const dev = Math.floor(minDev + Math.random() * maxDevNum);
                devs[devsIndex] = dev;
              }
            }

            stickyEndParts = _getJunctionStickyEndParts(
              fragments,
              hasStickyEnds,
              spacers,
              enzyme,
              (i: number) => devs[devsIndexMap[i]]
            );
            if (stickyEndParts == null) {
              continue;
            }
            const {validationScore} = validateStickyEnds(
              stickyEndParts.map((stickyEndPart) => stickyEndPart.stickyEnd),
              internalStickyEnds
            ) as FinalizedStickyEndValidationResult;
            if (bestValidationScore > validationScore) {
              bestValidationScore = validationScore;
              bestStickyEndParts = stickyEndParts;
            }
            if (bestValidationScore === 0) {
              newDeferred.resolve(bestStickyEndParts);
              return;
            }
          }
          newDeferred.reject();
        });
        return newDeferred.promise();
      }
    );
  }
  // @ts-expect-error: types/jquery does not allow null
  return designPromise.then(null, () => $.Deferred().resolve(bestStickyEndParts).promise());
}

function _designPrimers(
  fragments: Array<biojs.Sequence | null>,
  hasStickyEnds: Array<boolean>,
  spacers: Array<biojs.Sequence | null>,
  enzyme: Enzyme,
  options: FilledGoldenGateOptions,
  stickyEndParts: Array<StickyEndPart>
): biojs.Sequence {
  let trimmedFragmentEnd;
  let trimmedFragmentStart;
  let fragment;
  const prev = _makePrevIndexFunc(fragments);
  const next = _makeNextIndexFunc(fragments);

  const trimmedFragmentEnds: Array<null | Array<number>> = Array(fragments.length).fill(null);
  const primerPairs: Array<null | unknown> = Array(fragments.length).fill(null);
  for (let i = 0; i < fragments.length; i++) {
    fragment = fragments[i];
    if (hasStickyEnds[i]) {
      continue;
    }
    const prevSpacerBases = _spacerBasesAt(spacers, prev(i));
    const spacerBases = _spacerBasesAt(spacers, i);

    const {prevFragmentStart, prevSpacerStart, fragmentStart} = stickyEndParts[i];
    const forwardPreBindingRegionBases =
      fragments[prev(i)]!.bases().slice(prevFragmentStart) + prevSpacerBases.slice(prevSpacerStart);
    trimmedFragmentStart = fragmentStart;

    const {prevFragmentEnd, prevSpacerEnd, fragmentEnd} = stickyEndParts[next(i)];
    const reversePreBindingRegionBases =
      spacerBases.slice(0, prevSpacerEnd) + fragments[next(i)]!.bases().slice(0, fragmentEnd);
    trimmedFragmentEnd = prevFragmentEnd;

    // @ts-expect-error Introduced by Typescript 5.1 upgrade. `fragment` is possibly null, and a ! type
    // assertion causes an eslint error.
    const trimmedFragmentBases = fragment.bases().slice(trimmedFragmentStart, trimmedFragmentEnd);
    primerPairs[i] = _getPrimerPair(
      trimmedFragmentBases,
      enzyme,
      Object.assign({}, options, {
        forwardPreBindingRegionBases,
        reversePreBindingRegionBases,
        postRecognitionSiteBases: util.random(enzyme.postRecognitionSiteLength()),
      })
    );
    trimmedFragmentEnds[i] = [trimmedFragmentStart, trimmedFragmentEnd];
  }

  let _sequence = new biojs.Sequence();
  const junctions = [];
  const iterable = _.zip(fragments, spacers);
  for (let i = 0; i < iterable.length; i++) {
    let spacer;
    let junction5;
    let junction3;
    [fragment, spacer] = iterable[i];
    junction5 = _sequence.length();
    if (hasStickyEnds[i]) {
      if (hasStickyEnds[prev(i)]) {
        // Remove leading sticky end, since it's been repeated on the previous fragment.
        fragment = fragment.slice(enzyme.stickyEndLength());
      } else {
        // Junction starts after sticky end.
        junction5 += enzyme.stickyEndLength();
      }
    }
    _sequence = _sequence.concat(fragment);
    junction3 = _sequence.length();
    if (hasStickyEnds[i]) {
      junction3 -= enzyme.stickyEndLength();
    }
    if (spacer != null) {
      _sequence = _sequence.concat(spacer);
    }
    junctions.push([junction5, junction3]);
  }
  const newJunctions = [];
  for (const [junction5, junction3] of junctions) {
    newJunctions.push([junction5, sequtil.mod(junction3, _sequence.length())]);
  }
  // @ts-expect-error: Abuse of Backbone attributes
  _sequence.set({circular: true, junctions: newJunctions});

  const sequencePrimers = _sequence.sequencePrimers();
  const zipped = _.zip(
    primerPairs,
    fragments,
    newJunctions,
    colors.shuffledPastelColors.slice(0, fragments.length)
  );
  const pairIndices = zipped.map(([pair, frag, junction, color], idx) => {
    if (pair == null) {
      sequencePrimers.add({skip: true});
      sequencePrimers.add({skip: true});
      return null;
    }
    const {forward, reverse} = pair;
    const junction5 = junction[0];
    [trimmedFragmentStart, trimmedFragmentEnd] = trimmedFragmentEnds[idx]!;
    const fwdStart = sequtil.mod(junction5 + trimmedFragmentStart, _sequence.length());
    sequencePrimers.add({
      bases: forward.bases,
      start: fwdStart,
      end: sequtil.mod(fwdStart + forward.bindingLength, _sequence.length()),
      name: `${frag.name()} FWD`,
      color,
      strand: 1,
    });
    const revEnd = sequtil.mod(junction5 + trimmedFragmentEnd, _sequence.length());
    sequencePrimers.add({
      bases: reverse.bases,
      start: sequtil.mod(revEnd - reverse.bindingLength, _sequence.length()),
      end: revEnd,
      name: `${frag.name()} REV`,
      color,
      strand: -1,
    });
    return {
      fwd_index: idx * 2,
      rev_index: idx * 2 + 1,
    };
  });
  addPrimerNameSuffixes(sequencePrimers);
  // @ts-expect-error: Abuse of Backbone attributes
  _sequence.set({primerPairs: pairIndices});
  return _sequence;
}

interface GoldenGateOptions {
  preRecognitionSiteBases?: string;
  enzymeID?: number;
  preRecognitionSiteLength?: number;
  dryRun?: boolean;
}

export interface FilledGoldenGateOptions {
  preRecognitionSiteBases: string;
  enzymeID: number;
  preRecognitionSiteLength: number;
  dryRun: boolean;
  bindingRegionMinLength: number;
  bindingRegionMinTM: number;
  primerPairMaxTMDiff: number;
}

// Calculate primer pairs for each fragment that does not have sticky ends.
//   fragments, spacers: array of biojs.Sequence models
//   hasStickyEnds: array of Boolean values
//   options: {preRecognitionSiteLength, preRecognitionSiteBases, bindingRegionMinTM,
//             bindingRegionMinLength, primerPairMaxTMDiff, enzymeID, dryRun} object
function designGoldenGate(
  fragments: Array<biojs.Sequence | null>,
  hasStickyEnds: Array<boolean>,
  spacers: Array<biojs.Sequence | null>,
  options: GoldenGateOptions | null
): JQueryPromise<biojs.Sequence> | Array<Array<string>> {
  let i;
  if (options == null) {
    options = {};
  }
  const filledOptions: FilledGoldenGateOptions = _.defaults(options, {
    preRecognitionSiteLength: 6,
    bindingRegionMinTM: 50,
    bindingRegionMinLength: 18,
    primerPairMaxTMDiff: 5,
    enzymeID: 85, // BbsI
    dryRun: false,
  });
  const errors = [];
  const warnings: Array<string> = [];
  const enzyme = COMMON_ENZYMES.get(filledOptions.enzymeID);
  if (!enzyme) {
    throw new Error(`Invalid enzymeID: ${filledOptions.enzymeID}`);
  }
  // Want to override bases even if set but null, so can't use _.defaults.
  if (!filledOptions.preRecognitionSiteBases) {
    filledOptions.preRecognitionSiteBases = util.random(filledOptions.preRecognitionSiteLength);
  }
  assert(
    filledOptions.preRecognitionSiteBases.length === filledOptions.preRecognitionSiteLength,
    'Did not fill prerecognition site correctly'
  );

  // Calculate the internal sticky ends.
  const internalStickyEnds = [];
  for (const fragment of fragments) {
    if (fragment == null && filledOptions.dryRun) {
      continue;
    }
    const iterable = util.digest([fragment!], [enzyme.toJSON()]);
    for (i = 0; i < iterable.length; i++) {
      const digestedFragment = iterable[i];
      if (i === 0) {
        // Skip the first digested fragment because its start is
        // the start of the whole fragment, not a cut site.
        continue;
      }
      // Set name for sticky end to read.
      digestedFragment.set({name: fragment!.name()});
      internalStickyEnds.push(_startStickyEnd(digestedFragment, enzyme));
    }
  }

  const prev = _makePrevIndexFunc(fragments);

  // Validate the pre-existing junction sticky ends.
  for (let idx = 0; idx < fragments.length; idx++) {
    if (hasStickyEnds[idx] && hasStickyEnds[prev(idx)]) {
      const curStartStickyEnd = _startStickyEnd(fragments[idx]!, enzyme);
      const prevEndStickyEnd = _endStickyEnd(fragments[prev(idx)]!, enzyme);
      if (curStartStickyEnd.bases !== prevEndStickyEnd.bases) {
        const msg = `${fragments[idx]!.name()} starts with sticky end ${curStartStickyEnd.bases} \
but ${fragments[prev(idx)]!.name()} ends with sticky end ${prevEndStickyEnd.bases}.`;
        if (filledOptions.dryRun) {
          errors.push(msg);
        } else {
          return $.Deferred<biojs.Sequence>().reject(msg).promise();
        }
      }
    }
  }

  const preExistingJunctionStickyEnds = _.compact(
    _getPreExistingJunctionStickyEnds(fragments, hasStickyEnds, enzyme)
  );
  const validation = validateStickyEnds(
    preExistingJunctionStickyEnds,
    internalStickyEnds,
    filledOptions.dryRun
  ) as DryRunStickyEndValidationResult;
  Array.prototype.push.apply(errors, validation.errors);
  Array.prototype.push.apply(warnings, validation.warnings);

  // The dryRun option causes us to return the errors and warnings instead of proceeding with
  // generating the other sticky ends.
  if (filledOptions.dryRun) {
    return [errors, warnings];
  }

  return (
    _designStickyEnds(fragments, hasStickyEnds, spacers, enzyme, internalStickyEnds) as JQueryPromise<
      Array<StickyEndPart>
    >
  ).then((stickyEndParts: Array<StickyEndPart>) => {
    const deferred = $.Deferred<biojs.Sequence>();
    try {
      const _sequence = _designPrimers(
        fragments,
        hasStickyEnds,
        spacers,
        enzyme,
        filledOptions,
        stickyEndParts
      );
      deferred.resolve(_sequence);
    } catch (error) {
      if (!(error instanceof AssemblyError)) {
        throw error;
      }
      deferred.reject(error.message);
    }
    return deferred.promise();
  });
}

// Given junction data, return start and end of its sticky end.
function _getStickyEndRange(
  sequence: biojs.Sequence,
  fwd: null | biojs.SequencePrimer,
  rev: null | biojs.SequencePrimer,
  junction3: number | null
): Array<number | null> {
  if (fwd == null && rev == null && junction3 == null) {
    return [null, null];
  }
  const enzyme = sequence.assembly()!.enzyme();
  const stickyEndStart =
    fwd != null
      ? sequtil.mod(
          fwd.overhangEdge(sequence)! +
            fwd.bases().indexOf(enzyme.get('seq')) +
            enzyme.recognitionSiteLength() +
            enzyme.postRecognitionSiteLength(),
          sequence.length()
        )
      : rev != null
        ? sequtil.mod(
            rev.overhangEdge(sequence)! -
              rev.bases().indexOf(enzyme.get('seq')) -
              enzyme.recognitionSiteLength() -
              enzyme.postRecognitionSiteLength() -
              enzyme.stickyEndLength(),
            sequence.length()
          )
        : junction3;
  const stickyEndEnd = sequtil.mod(stickyEndStart! + enzyme.stickyEndLength(), sequence.length());
  return [stickyEndStart, stickyEndEnd];
}

// All starts and ends of the sequence's sticky ends.
function stickyEndRanges(sequence: biojs.Sequence): Array<{
  start: number | null;
  end: number | null;
  upstreamFragment: unknown;
  downstreamFragment: unknown;
}> {
  const fragmentResults = sequence.fragmentResults();
  const next = _makeNextIndexFunc(fragmentResults);
  return fragmentResults.map((fragmentResult, i) => {
    const nextFragmentResult = fragmentResults.at(next(i));
    const rev = getFlankingPrimers(sequence, fragmentResult)[1];
    const nextFwd = getFlankingPrimers(sequence, nextFragmentResult)[0];
    const [start, end] = _getStickyEndRange(sequence, nextFwd, rev, fragmentResult.end());
    return {
      start,
      end,
      upstreamFragment: fragmentResult,
      downstreamFragment: nextFragmentResult,
    };
  });
}

const plugin: PrimerPlugin = Object.assign({}, primerPlugin, {
  assemblyType: ASSEMBLY_TYPE.GOLDEN_GATE,

  attachTabs(sequence: biojs.Sequence, buffer: BufferState): void {
    buffer.add(new GoldenGateTabState({sequence}));
  },

  checkAssembly(assembly: Assembly) {
    let errors = [];
    let warnings: Array<string> = [];
    if (
      !assembly
        .slots()
        .withoutSpacers()
        .every((slot: AssemblySlotType) => !slot.isEmpty())
    ) {
      errors.push('A backbone or insert is unset.');
    }
    if (!validSpacerOrder(assembly.slots())) {
      errors.push('Spacer sequences must be adjacent to at least one fragment generated by PCR.');
    }
    const assemblyEnzyme = assembly.enzyme();

    const [fragments, hasStickyEnds] = slotsToFragments(assembly.slots().withoutSpacers());
    const [designErrors, designWarnings] = designGoldenGate(
      fragments,
      hasStickyEnds,
      assembly.slots().getSpacers(),
      Object.assign({dryRun: true}, assembly.parameters())
    ) as Array<Array<string>>;
    errors = errors.concat(designErrors);
    warnings = warnings.concat(designWarnings);

    // Require all fragments be made with the same enzyme.
    assembly.slots().each((slot: AssemblySlotType) => {
      if (slot.isEmpty()) {
        return;
      }
      const fragment = slot.singleFragment();
      const enzymeIDs = fragment.enzymeIDs();
      if (!enzymeIDs) {
        return;
      }
      for (const enzyme of enzymeIDs.map((enzymeID) => COMMON_ENZYMES.get(enzymeID))) {
        if (enzyme.id !== assemblyEnzyme.id) {
          errors.push(`${slot.displayName()} was cut with ${enzyme.get('name')} - must use \
${assemblyEnzyme.get('name')}`);
        }
      }
    });

    const onlyDesignErrors = errors.length === designErrors.length;
    return {canCreateWithErrors: onlyDesignErrors, errors, warnings};
  },

  _getTwoPrimerJunctionRanges(
    sequence: biojs.Sequence,
    fwd: biojs.SequencePrimer,
    rev: biojs.SequencePrimer
  ): Array<Array<number>> {
    // Union of the two primers' ranges.
    return [[rev.start(), fwd.end()]];
  },

  _canInsert(sequence: biojs.Sequence, selection: Selection, errback: (message: string) => void): boolean {
    // Completely disable inserts in Golden Gate because sticky-end calculation depends on inserted bases.
    // Users should be using pre-construction spacers instead.
    errback('To insert on a Golden Gate assembly, insert spacer slots prior to sequence creation.');
    return false;
  },

  canDelete(sequence: biojs.Sequence, selection: Selection, errback: (message: string) => void): boolean {
    if (sequence.finalized()) {
      return true;
    }
    // Completely disable deletes in Golden Gate because users can only delete inserted bases, but inserts
    // are also disabled.
    errback('To delete bases, finalize the sequence from the results tab.');
    return false;
  },

  didDelete(
    sequence: biojs.Sequence,
    selection: Selection,
    deletionLength: number,
    callback: (message: string) => void,
    errback: (message: string) => void
  ): void {
    if (sequence.finalized()) {
      return;
    }
    logging.error('didDelete was called in GG assembly, but delete should not be allowed!');
  },

  didInsert(
    sequence: biojs.Sequence,
    insertPos: number,
    bases: string,
    callback: (message: string) => void,
    errback: (message: string) => void
  ): void {
    if (sequence.finalized()) {
      return;
    }
    logging.error('didInsert was called in GG assembly, but insert should not be allowed!');
  },

  didReplace(
    sequence: biojs.Sequence,
    selection: Selection,
    bases: string,
    callback: (message: string) => void,
    errback: (message: string) => void
  ): void {
    if (sequence.finalized()) {
      return;
    }
    // @ts-expect-error: `_findReplaceRange` comes from the primer plugin
    const range = this._findReplaceRange(sequence, selection);
    assert(range != null, 'didReplace found no range even though _canInitiateReplace passed');
    const fwdPrimer = range[2];
    const revPrimer = range[3];
    const junction3 = range[4];
    const [stickyEndStart, stickyEndEnd] = _getStickyEndRange(sequence, fwdPrimer, revPrimer, junction3);
    if (!(fwdPrimer != null || revPrimer != null)) {
      return;
    }

    const selectionStart = selection.modelStart();
    const selectionEnd = selection.modelEnd();

    function replaceRegion(regionStart: number, regionEnd: number): void {
      let primerBases;
      if (!sequtil.isRangeOverlap(selectionStart!, selectionEnd!, regionStart, regionEnd)) {
        return;
      }
      const regionSelStart = sequtil.baseInRange(selectionStart!, regionStart, regionEnd)
        ? selectionStart
        : regionStart;
      const regionSelEnd = sequtil.baseInRange(selectionEnd!, regionStart, regionEnd)
        ? selectionEnd
        : regionEnd;
      const regionBases = bases.slice(regionSelStart! - selectionStart!, regionSelEnd! - selectionStart!);
      const shouldUpdateFwdPrimer = (): boolean =>
        sequtil.baseInRange(regionSelStart!, stickyEndStart!, fwdPrimer.end());
      if (fwdPrimer != null && shouldUpdateFwdPrimer()) {
        primerBases = replacePrimerRegion(
          sequence,
          fwdPrimer,
          regionBases,
          fwdPrimer.overhangEdge(sequence),
          regionSelStart,
          regionSelEnd
        );
        fwdPrimer.set({bases: primerBases});
      }
      const shouldUpdateRevPrimer = (): boolean =>
        sequtil.baseInRange(regionSelStart!, revPrimer.start(), stickyEndEnd!);
      if (revPrimer != null && shouldUpdateRevPrimer()) {
        primerBases = replacePrimerRegion(
          sequence,
          revPrimer,
          util.revcomp(regionBases, DNA_COMPLEMENT_TABLE),
          revPrimer.overhangEdge(sequence),
          regionSelEnd,
          regionSelStart
        );
        revPrimer.set({bases: primerBases});
      }
    }

    // Break up selection into 3 regions:
    //   1. before the sticky end - replace bases in the reverse primer
    //   2. in the sticky end - replace bases in both primers
    //   3. after the sticky end - replace bases in the forward primer
    if (revPrimer != null) {
      replaceRegion(revPrimer.start(), stickyEndStart!);
    }
    replaceRegion(stickyEndStart!, stickyEndEnd!);
    if (fwdPrimer != null) {
      replaceRegion(stickyEndEnd!, fwdPrimer.end());
    }

    $.when(fwdPrimer != null ? fwdPrimer.save(null, {fileUpdate: true}) : undefined)
      .then(() => (revPrimer != null ? revPrimer.save(null, {fileUpdate: true}) : undefined))
      .done(() => {
        callback('Primers successfully updated.');
      })
      .fail(() => {
        errback('Error updating primers.');
      });
  },
});

export {slotsToFragments, stickyEndRanges, designGoldenGate, plugin, StickyEnd, validateStickyEnds};
